/**
 * @fileoverview Admin Project Detail Page - Refactored
 *
 * Main container page for project detail view with sections:
 * 1. Project Info Header - Key project details and client info
 * 2. Quick Progress Update - Phase/progress editing
 * 3. Plans & Documents - Floorplans, site plans, other docs
 * 4. Project Tasks - Task management with status tabs
 * 5. Progress Photos - Photo gallery by category
 * 6. Home Selections - Read-only client selections
 * 7. Change Orders - List with impact summary
 *
 * @version 2.0.0 - Refactored into modular components
 */

import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSpinner } from "react-icons/fa";
import "./AdminProjectDetails.css";

// Section Component Imports
import ProjectInfoHeader from "./components/ProjectInfoHeader";
import QuickProgressUpdate from "./components/QuickProgressUpdate";
import PlansDocuments from "./components/PlansDocuments";
import ProjectTasks from "./components/ProjectTasks";
import ProgressPhotos from "./components/ProgressPhotos";
import HomeSelections from "./components/HomeSelections";
import ChangeOrders from "./components/ChangeOrders";
import ChangeOrderModal from "../../../components/admin/ChangeOrderModal";

// Data hook imports
import { usePlanDocuments } from "../../../hooks/useProjects";
import { useProjectTasks } from "../../../hooks/useTasks";
import { useClientSelectionsData } from "../../../hooks/useSelections";
import {
  useChangeOrders,
  useCreateChangeOrder,
  useUpdateChangeOrder,
  useChangeOrderFieldOptions,
} from "../../../hooks/useChangeOrders";
import {
  useAdminProject,
  useUpdateProject,
} from "../../../hooks/useAdminProjects";

/**
 * AdminProjectDetails Component
 * Main container for project detail page
 */
const AdminProjectDetails = ({ userData }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Scroll to section when ?tab= query param is present
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) {
      const timer = setTimeout(() => {
        const el = document.getElementById(`apd-${tab}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  /* ============================================
     DATA FETCHING
     ============================================ */

  // Main project data
  const {
    data: project,
    isLoading: projectLoading,
    refetch: refetchProject,
  } = useAdminProject(projectId);

  // Section data
  const {
    data: planDocs = { floorplans: [], siteplans: [], other: [] },
    isLoading: plansLoading,
    refetch: refetchPlans,
  } = usePlanDocuments(projectId);
  const { data: tasksData = [] } = useProjectTasks(projectId);
  const { data: selectionsData, isLoading: selectionsLoading } =
    useClientSelectionsData(projectId);
  const { data: changeOrders = [], refetch: refetchChangeOrders } =
    useChangeOrders(projectId);
  const { data: coFieldOptions } = useChangeOrderFieldOptions();

  // Mutations
  const updateProjectMutation = useUpdateProject();
  const createCOMutation = useCreateChangeOrder(projectId);
  const updateCOMutation = useUpdateChangeOrder(projectId);

  // Extract Data
  const tasks = useMemo(() => {
    return tasksData?.data || tasksData || [];
  }, [tasksData]);

  /* ============================================
     STATE MANAGEMENT
     ============================================ */

  // Plans & Documents states
  const [activePlanTab, setActivePlanTab] = useState("floorplans");

  // Change Order modal states
  const [showCOModal, setShowCOModal] = useState(false);
  const [editingCO, setEditingCO] = useState(null);

  /* ============================================
     EVENT HANDLERS
     ============================================ */

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    navigate("/admin/projects");
  };

  // Handler for updating project
  const handleUpdateProject = async (updates) => {
    await updateProjectMutation.mutateAsync({
      projectId,
      updates,
    });
    // Optionally refetch to get latest data
    await refetchProject();
  };

  /* ============================================
     LOADING & ERROR STATES
     ============================================ */

  if (projectLoading) {
    return (
      <div className="loading-container">
        <FaSpinner className="admin-dash-spinner" />
        <span className="loading-text">Loading project information...</span>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="apd-error">
        <p>Project not found</p>
        <button className="apd-btn-primary" onClick={handleBack}>
          Back to Projects
        </button>
      </div>
    );
  }

  /* ============================================
     RENDER
     ============================================ */

  return (
    <div className="admin-project-detail">
      {/* Back Navigation */}
      <button className="apd-back-btn" onClick={handleBack}>
        <FaArrowLeft />
        <span>Back to Projects</span>
      </button>
      <div className="apd-container">
        {/* ========== SECTION 1: PROJECT INFO ========== */}
        <div id="apd-info">
          <ProjectInfoHeader
            projectId={projectId}
            project={project}
            onUpdate={handleUpdateProject}
            isUpdating={updateProjectMutation.isPending}
          />
        </div>

        {/* ========== SECTION 2: QUICK PROGRESS UPDATE ========== */}
        <div id="apd-progress">
          <QuickProgressUpdate
            projectId={projectId}
            project={project}
            onUpdate={async (updates) => {
              await updateProjectMutation.mutateAsync({
                projectId,
                updates,
              });
            }}
            isUpdating={updateProjectMutation.isPending}
          />
        </div>

        {/* ========== SECTION 3: PLANS & DOCUMENTS ========== */}
        <div id="apd-plans">
          <PlansDocuments
            projectId={projectId}
            projectName={project?.projectName}
            plans={planDocs}
            isLoading={plansLoading}
            activePlanTab={activePlanTab}
            setActivePlanTab={setActivePlanTab}
            onUploadComplete={refetchPlans}
          />
        </div>

        {/* ========== SECTION 4: PROJECT TASKS ========== */}
        <div id="apd-tasks">
          <ProjectTasks
            projectId={projectId}
            projectName={project?.projectName}
            tasks={tasks}
            createTaskList={project?.createTaskList || false}
            onUpdateProject={handleUpdateProject}
            isUpdating={updateProjectMutation.isPending}
          />
        </div>

        {/* ========== SECTION 5: PROGRESS PHOTOS ========== */}
        <div id="apd-photos">
          <ProgressPhotos projectId={projectId} project={project} />
        </div>

        {/* ========== SECTION 6: HOME SELECTIONS ========== */}
        <div id="apd-selections">
          <HomeSelections
            projectId={projectId}
            projectName={project?.projectName}
            projectType={project?.projectType}
            selectionsData={selectionsData}
            isLoading={selectionsLoading}
            onUploadComplete={() => {
              // Optional: Add any post-upload logic here
              // React Query will auto-refetch if configured
            }}
          />
        </div>

        {/* ========== SECTION 7: CHANGE ORDERS ========== */}
        <div id="apd-change-orders">
          <ChangeOrders
            changeOrders={changeOrders}
            onNewCO={() => {
              setEditingCO(null);
              setShowCOModal(true);
            }}
            onEditCO={(co) => {
              setEditingCO(co);
              setShowCOModal(true);
            }}
            onRefresh={refetchChangeOrders}
          />
        </div>
      </div>

      {/* Change Order Modal */}
      <ChangeOrderModal
        isOpen={showCOModal}
        onClose={() => {
          setShowCOModal(false);
          setEditingCO(null);
        }}
        projectId={projectId}
        projectName={project?.projectName}
        changeOrder={editingCO}
        onSave={async (data, changeOrderId) => {
          if (changeOrderId) {
            await updateCOMutation.mutateAsync({
              changeOrderId,
              updates: data,
            });
          } else {
            await createCOMutation.mutateAsync(data);
          }
        }}
        isSaving={createCOMutation.isPending || updateCOMutation.isPending}
        fieldOptions={coFieldOptions}
      />
    </div>
  );
};

export default AdminProjectDetails;
